/**
 * Paleta de blocos. Arrastar para o canvas ou clicar para inserir no centro.
 *
 * Os dois caminhos existem porque arrastar-e-soltar é o pedido do produto, mas clicar é o que
 * funciona em trackpad ruim, tela pequena e navegação por teclado.
 *
 * **Agrupada por família**, e não uma lista corrida de sete. Sete itens iguais em sequência não
 * têm forma: para achar o webhook de backend era preciso ler os sete rótulos. Com três grupos
 * rotulados, a busca começa em "integração" e termina em três itens — é o princípio de região comum
 * da Gestalt fazendo o trabalho que a leitura fazia.
 */

import { BLOCK_FAMILIES, BLOCK_META, blockChip, blocksOfFamily, FAMILY_ORDER } from '../flow/mapping.js';
import { BLOCK_ICONS } from './icons.js';
import { RAIO } from './ui.js';
import type { BlockKind } from '@merlin/flow-engine';

export const BLOCK_DRAG_TYPE = 'application/merlin-block';

export function Palette({ onInsert }: { onInsert(type: BlockKind): void }) {
  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="text-sm font-semibold text-slate-900">Blocos</p>
        <p className="text-xs text-slate-500">Arraste para o canvas ou clique para inserir</p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-3">
        {FAMILY_ORDER.map((family) => {
          const familia = BLOCK_FAMILIES[family];
          return (
            <section key={family} className="space-y-1">
              <h3
                className="px-1 pb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase"
                title={familia.description}
              >
                {familia.label}
              </h3>

              {blocksOfFamily(family).map((type) => {
                const meta = BLOCK_META[type];
                const Icone = BLOCK_ICONS[type];
                return (
                  <button
                    key={type}
                    type="button"
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData(BLOCK_DRAG_TYPE, type);
                      event.dataTransfer.effectAllowed = 'move';
                    }}
                    onClick={() => onInsert(type)}
                    title={meta.description}
                    className={`flex w-full cursor-grab items-center gap-3 border border-transparent px-2 py-2 text-left transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 active:cursor-grabbing ${RAIO.controle}`}
                  >
                    {/* Mesmo ícone e mesma cor do bloco no canvas: o que se arrasta daqui é reconhecível lá. */}
                    <span className={`grid size-6 shrink-0 place-items-center ${RAIO.selo} ${blockChip(type)}`}>
                      <Icone size={14} />
                    </span>
                    {/*
                      A descrição saiu daqui. Sete descrições empilhadas eram 14 linhas de texto numa
                      coluna de 224px, e nenhuma delas se lê ao procurar um bloco — vira parede. Ela
                      continua no `title` e, inteira, no inspector quando o bloco é selecionado.
                    */}
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
    </aside>
  );
}
